/** Characters: bodies, gravity fields, teleports, and stepping a player (walking, climbing, swimming, drifting, the grapple, helm). */
import { CHARACTER, CURRENTS, GRAPPLE, GRAVITY, GRIP, HEALTH, SHIP, STEAMPACK, STREAM, VINES, WORLD_GEN } from "../constants";
import { wearsSteampack } from "../items";
import { clamp, v2, vAdd, vDist, vDot, vLen, vNorm, vRotate, vScale, vSub, wrapAngle, type Box, type Vec2 } from "../math";
import { T, blocksShips, inGrid, isClimbable, isHelm, isSolid, openToOutside, pieceAnchor, pieceDef, pieceToGrid, tileAt, turnAt, type Grid } from "../tiles";
import { asteroidPulls, type AsteroidDef, type PlayerInput, type PlayerState, type Tether } from "./types";
import { currentAt } from "./generate";
import { flowAt } from "./flow";
import { vineCurve, vineLength, vineNear, vinePoint, vineTangent } from "./vines";
import { type ShipBody, type AsteroidBody, type Sim, asteroidSolidAt, shipCellAt, shipToWorld, worldToShip, shipCenter } from "./ships";
import { boxHitsShipTiles, circleHitsGrid, blockedForCharacter, rockHitsCharacter, sweep } from "./collision";
import { hostId, hostToWorld, turretHostOf, turretOf, turretAimAt, seatHere } from "./turrets";

/**
 * Where a grapple thrown from `from` toward `aim` hooks, within `range`: into
 * rock, or (`ships`) into a ship, as a point in its grid. Null if it hits
 * nothing. `skip`: a ship it passes through (the one it's fired from).
 */
export function grappleHit(sim: Sim, from: Vec2, aim: Vec2, range: number = GRAPPLE.range, ships = true, skip?: string): { at: Vec2; ship?: string; rock?: string } | null {
  const dir = vNorm(vSub(aim, from));
  if (!dir.x && !dir.y) return null;
  for (let d = 1; d <= range; d += 0.4) {
    const at = vAdd(from, vScale(dir, d));
    if (asteroidSolidAt(sim, at)) {
      // Into a drifting rock: a point of its grid, so the line goes where it goes.
      const rock = sim.asteroids.find((a) => a.def.drift && blocksShips(tileAt(a.grid, Math.floor(at.x - a.def.ox), Math.floor(at.y - a.def.oy))));
      return rock ? { at: v2(at.x - rock.def.ox, at.y - rock.def.oy), rock: rock.def.id } : { at };
    }
    if (!ships) continue;
    for (const s of sim.ships.values()) {
      if (s.state.id !== skip && shipCellAt(s, at) >= 0) return { at: worldToShip(s, at), ship: s.state.id };
    }
  }
  return null;
}

/** Where a player's grapple is hooked, in the world (it moves with a ship or drifting rock it's hooked into), or null. */
export function grapplePoint(p: PlayerState, sim: Sim): Vec2 | null {
  if (!p.grapple) return null;
  if (p.grappleRock) return hookPoint(p.grapple, p.grappleRock, sim);
  if (!p.grappleShip) return p.grapple;
  const ship = sim.ships.get(p.grappleShip);
  return ship ? shipToWorld(ship, p.grapple) : null;
}

/**
 * Where a harpoon's line is stuck, in the world (see Tether): a point, or a
 * point of the drifting rock or ship it's in, wherever that is now; or where
 * the creature it's in was last. Null if the rock or ship isn't here.
 */
export function tetherPoint(t: Tether, sim: Sim): Vec2 | null {
  if (t.ship) {
    const ship = sim.ships.get(t.ship);
    return ship ? shipToWorld(ship, t.point) : null;
  }
  return hookPoint(t.point, t.rock, sim);
}

/**
 * Where a hook is in the world: `point` itself, or (hooked into drifting
 * rock `rock`, see grappleHit) that point of the rock's grid, wherever the
 * rock is now. Null if the rock isn't here.
 */
export function hookPoint(point: Vec2, rock: string | null | undefined, sim: Sim): Vec2 | null {
  if (!rock) return point;
  const a = sim.asteroids.find((x) => x.def.id === rock);
  return a ? v2(a.def.ox + point.x, a.def.oy + point.y) : null;
}

/** Head direction for a character turned by `up` radians (0 = world up). */
export function upVector(up: number): Vec2 {
  return v2(-Math.sin(up), Math.cos(up));
}

/** The character's right, perpendicular to upVector. */
export function rightVector(up: number): Vec2 {
  return v2(Math.cos(up), Math.sin(up));
}

/** Corners of a character's body standing at `feet`, turned by `up`. */
export function bodyCorners(feet: Vec2, up: number): Vec2[] {
  const hw = CHARACTER.width / 2;
  const r = vScale(rightVector(up), hw);
  const u = vScale(upVector(up), CHARACTER.height);
  return [vSub(feet, r), vAdd(feet, r), vAdd(vAdd(feet, r), u), vAdd(vSub(feet, r), u)];
}

export function boundsOf(points: Vec2[]): Box {
  const xs = points.map((q) => q.x);
  const ys = points.map((q) => q.y);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/** Bounding box of a character's body (exact when upright). */
export function characterBox(feet: Vec2, up = 0): Box {
  if (up === 0) {
    const hw = CHARACTER.width / 2;
    return { x0: feet.x - hw, y0: feet.y, x1: feet.x + hw, y1: feet.y + CHARACTER.height };
  }
  return boundsOf(bodyCorners(feet, up));
}

/** Feet position of a player in world space. */
export function playerWorldPos(p: PlayerState, sim: Sim): Vec2 {
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  return ship ? shipToWorld(ship, p.position) : p.position;
}

export function playerWorldCenter(p: PlayerState, sim: Sim): Vec2 {
  const local = vAdd(p.position, vScale(upVector(p.up), CHARACTER.height / 2));
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  return ship ? shipToWorld(ship, local) : local;
}

/** World-space bounding box of a player's body, whether aboard a ship or standing on a curved asteroid. */
export function playerWorldBox(p: PlayerState, sim: Sim): Box {
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  const corners = bodyCorners(p.position, p.up);
  return boundsOf(ship ? corners.map((q) => shipToWorld(ship, q)) : corners);
}

/** Where a character stands when they board a ship: at the helm, or the middle if there is none. */
export function boardingSpot(grid: Grid): Vec2 {
  // Row by row from the bottom, the first helm tile is a helm's anchor.
  const i = grid.tiles.findIndex(isHelm);
  if (i < 0) return v2(grid.w / 2, grid.h / 2);
  // In front of the middle of the helm, on the floor.
  const x = (i % grid.w) + pieceDef(T.IRONCLAD_HELM).w / 2;
  let y = Math.floor(i / grid.w);
  while (y > 0 && !isSolid(tileAt(grid, Math.floor(x), y - 1))) y--;
  return v2(x, y);
}

/** Put a character into a ship's frame, given their body centre in ship coordinates. */
export function enterShip(p: PlayerState, ship: ShipBody, localCenter: Vec2): void {
  p.frame = ship.state.id;
  p.launched = false;
  p.up = 0; // aboard, "up" is the ship's own up
  p.position = v2(localCenter.x, localCenter.y - CHARACTER.height / 2);
  p.velocity = vRotate(vSub(p.velocity, ship.state.velocity), -ship.state.angle);
}

/** Take a character out of their ship's frame into world space, keeping their motion and the ship's tilt. */
export function leaveShip(p: PlayerState, ship: ShipBody): void {
  const center = shipToWorld(ship, v2(p.position.x, p.position.y + CHARACTER.height / 2));
  p.up = wrapAngle(ship.state.angle);
  p.position = vSub(center, vScale(upVector(p.up), CHARACTER.height / 2));
  p.velocity = vAdd(vRotate(p.velocity, ship.state.angle), ship.state.velocity);
  p.frame = null;
  p.piloting = false;
}

// --------------------------------------------------------------- stepping

/** The middle of the ladder at (x, y) in a grid anchored at (ox, oy) (across its full width), or null. */
export function ladderIn(grid: Grid, ox: number, oy: number, x: number, y: number): Vec2 | null {
  const tx = Math.floor(x - ox);
  const ty = Math.floor(y - oy);
  if (!isClimbable(tileAt(grid, tx, ty))) return null;
  // A ladder can be several tiles wide (wide enough for a body): climb up its middle.
  let left = tx;
  let right = tx;
  while (isClimbable(tileAt(grid, left - 1, ty))) left--;
  while (isClimbable(tileAt(grid, right + 1, ty))) right++;
  return v2(ox + (left + right + 1) / 2, oy + ty + 0.5);
}

export function asteroidCenter(def: AsteroidDef): Vec2 {
  return v2(def.ox + def.w / 2, def.oy + def.h / 2);
}

/** How far from its middle an asteroid's gravity reaches: its widest possible outline, its branches, and WORLD_GEN.gravityMargin past that (more for a bigger rock). */
export function gravityReach(def: AsteroidDef): number {
  const [lo, hi] = WORLD_GEN.gravityMargin;
  const k = Math.min(1, Math.max(0, (def.radius - GRAVITY.minRadius) / (WORLD_GEN.ring.giant.radius - GRAVITY.minRadius)));
  return def.radius * 1.25 + lo + (hi - lo) * k + (def.reach ?? 0);
}

/**
 * The asteroid whose gravity reaches `point` (a circle a little past its
 * widest possible outline, and its branches), if any: where fields overlap,
 * the one `point` is deepest in. Only rocks that pull (see asteroidPulls).
 */
export function gravityFieldAt(sim: Sim, point: Vec2): AsteroidBody | undefined {
  let best: AsteroidBody | undefined;
  let bestDepth = 0;
  for (const a of sim.asteroids) {
    if (!asteroidPulls(a.def)) continue;
    const r = gravityReach(a.def);
    const c = asteroidCenter(a.def);
    const depth = r - Math.hypot(point.x - c.x, point.y - c.y);
    if (depth > bestDepth) [best, bestDepth] = [a, depth];
  }
  return best;
}

export function inGravityField(sim: Sim, point: Vec2): boolean {
  return gravityFieldAt(sim, point) !== undefined;
}

/** The angle (radians, 0 = +Y, as PlayerState.up) pointing straight out from asteroid `a`'s centre at `point`. */
export function outwardOn(a: AsteroidDef, point: Vec2): number {
  const c = asteroidCenter(a);
  return Math.atan2(-(point.x - c.x), point.y - c.y);
}

/** `angle` snapped to the nearest quarter turn (one of the grid's four directions), in (-PI, PI]. */
export const quarterTurn = (angle: number): number => wrapAngle(Math.round(angle / (Math.PI / 2)) * (Math.PI / 2));

/**
 * The `up` of someone standing at `point` on asteroid `a`: out from its
 * centre, snapped to the nearest of the grid's four directions, so floors are
 * rows and walls are columns wherever you stand (see GRAVITY). Given the up
 * they have now (`current`, a quarter turn), it's kept until the outward
 * angle is GRAVITY.turnBand past the diagonal, so nobody flaps at a valley.
 */
export function upOnAsteroid(a: AsteroidBody, point: Vec2, current?: number): number {
  const out = outwardOn(a.def, point);
  if (current !== undefined) {
    const held = quarterTurn(current);
    if (Math.abs(wrapAngle(current - held)) < 1e-6 && Math.abs(wrapAngle(out - held)) <= Math.PI / 4 + GRAVITY.turnBand) return held;
  }
  return quarterTurn(out);
}

/** Is a world point inside a water tile? */
export function waterAt(sim: Sim, point: Vec2): boolean {
  for (const a of sim.asteroids) {
    if (a.def.kind !== "water") continue;
    if (tileAt(a.grid, Math.floor(point.x - a.def.ox), Math.floor(point.y - a.def.oy)) === T.WATER) return true;
  }
  return false;
}

/** How much of a ship (0..1 of its tiles) is under water. Slows it down in applyShipControl. */
export function shipWaterFraction(sim: Sim, ship: ShipBody): number {
  if (!ship.stats.cells.length) return 0;
  const c = shipCenter(ship);
  const near = sim.asteroids.some(
    (a) => a.def.kind === "water" && vDist(c, v2(a.def.ox + a.def.w / 2, a.def.oy + a.def.h / 2)) < a.def.w * 0.71 + ship.stats.radius,
  );
  if (!near) return 0;
  let wet = 0;
  for (const i of ship.stats.cells) {
    if (waterAt(sim, shipToWorld(ship, v2((i % ship.grid.w) + 0.5, Math.floor(i / ship.grid.w) + 0.5)))) wet++;
  }
  return wet / ship.stats.cells.length;
}

/** Does any of a ship's tiles overlap asteroid rock? */
export function shipHitsRock(sim: Sim, ship: ShipBody): boolean {
  for (const i of ship.stats.cells) {
    const w = shipToWorld(ship, v2((i % ship.grid.w) + 0.5, Math.floor(i / ship.grid.w) + 0.5));
    for (const a of sim.asteroids) {
      if (circleHitsGrid(w.x, w.y, SHIP.cellRadius, a.grid, a.def.ox, a.def.oy, blocksShips)) return true;
    }
  }
  return false;
}

/**
 * The spot nearest `target` where `fits` holds, searched in rings `step`
 * apart outward from it. Null if nothing within `maxRadius` fits.
 */
export function clearSpotNear(target: Vec2, fits: (p: Vec2) => boolean, step: number, maxRadius = 800): Vec2 | null {
  if (fits(target)) return { ...target };
  for (let r = step; r <= maxRadius; r += step) {
    const n = Math.ceil((2 * Math.PI * r) / step);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const p = v2(target.x + Math.cos(a) * r, target.y + Math.sin(a) * r);
      if (fits(p)) return p;
    }
  }
  return null;
}

/**
 * Is `point` inside a sealed pocket of an asteroid (a cave nobody has dug
 * into yet)? Teleports never land there. Each grid's flood fill is done once
 * per returned function.
 */
export function sealedInRock(sim: Sim): (point: Vec2) => boolean {
  const open = new Map<string, Uint8Array>();
  return (point) =>
    sim.asteroids.some((a) => {
      const x = Math.floor(point.x - a.def.ox);
      const y = Math.floor(point.y - a.def.oy);
      if (!inGrid(a.grid, x, y)) return false;
      let o = open.get(a.def.id);
      if (!o) open.set(a.def.id, (o = openToOutside(a.grid)));
      return !o[y * a.grid.w + x];
    });
}

/**
 * Teleport a character on foot to the open spot nearest `target` (their
 * feet), turned to stand on any asteroid whose gravity reaches there.
 * Returns false, leaving them where they were, if there is no room.
 */
export function teleportPlayer(p: PlayerState, target: Vec2, sim: Sim): boolean {
  const upAt = (feet: Vec2) => {
    const a = gravityFieldAt(sim, feet);
    return a ? upOnAsteroid(a, feet) : 0;
  };
  const sealed = sealedInRock(sim);
  const fits = (f: Vec2) => {
    const up = upAt(f);
    return !blockedForCharacter(f, up, sim) && !sealed(vAdd(f, vScale(upVector(up), CHARACTER.height / 2)));
  };
  const spot = clearSpotNear(target, fits, 3);
  if (!spot) return false;
  p.frame = null;
  p.piloting = false;
  p.position = spot;
  p.velocity = v2();
  p.up = upAt(spot);
  p.grounded = false;
  p.launched = false;
  // Whatever held them where they were lets go: a vine, a grapple line, a turret seat (the server frees the gun).
  p.vine = null;
  p.vineT = undefined;
  dropGrapple(p);
  p.gunning = null;
  p.gunningAsteroid = undefined;
  return true;
}

/**
 * Teleport a ship, and so everyone aboard, to the open spot nearest `target`
 * (its centre): clear of rock, suns, the shell and other ships. Returns
 * false, leaving it where it was, if there is no room.
 */
export function teleportShip(ship: ShipBody, target: Vec2, sim: Sim): boolean {
  const half = v2(ship.grid.w / 2, ship.grid.h / 2);
  const r = ship.stats.radius;
  const sealed = sealedInRock(sim);
  const fits = (c: Vec2) => {
    if (sealed(c)) return false;
    if (vLen(c) + r > sim.world.worldRadius) return false;
    if (sim.world.suns.some((s) => vDist(s.position, c) < s.radius + r)) return false;
    for (const other of sim.ships.values()) {
      if (other.state.id !== ship.state.id && vDist(shipCenter(other), c) < other.stats.radius + r) return false;
    }
    return !shipHitsRock(sim, { ...ship, state: { ...ship.state, position: vSub(c, half) } });
  };
  const spot = clearSpotNear(target, fits, 6);
  if (!spot) return false;
  ship.state.position = vSub(spot, half);
  ship.state.velocity = v2();
  ship.state.angularVelocity = 0;
  return true;
}

export function helmUnder(p: PlayerState, ship: ShipBody): boolean {
  const t = tileAt(ship.grid, Math.floor(p.position.x), Math.floor(p.position.y + CHARACTER.height / 2));
  return isHelm(t);
}

export function approach(v: number, target: number, maxDelta: number): number {
  return v < target ? Math.min(target, v + maxDelta) : Math.max(target, v - maxDelta);
}

/**
 * Advance one player by `dt` seconds using `input`. When piloting, this flies
 * their ship instead. Pure and deterministic: the client runs it for
 * prediction, the server for truth.
 *
 * Aboard a ship everything happens in the ship's own coordinates, and only its
 * tiles collide: there's no gravity, and the crew crawl about holding onto it
 * (see GRIP) however the ship is turned.
 */
export function stepPlayer(p: PlayerState, input: PlayerInput, dt: number, sim: Sim): void {
  const wasGrounded = p.grounded;
  const frame = p.frame;
  // How fast they were heading feet-first, in case this step lands them.
  const falling = -vDot(p.velocity, upVector(p.up));
  movePlayer(p, input, dt, sim);
  // Hitting the ground hard hurts.
  if (!wasGrounded && p.grounded && p.frame === frame && falling > HEALTH.safeLanding) {
    hurt(p, (falling - HEALTH.safeLanding) * HEALTH.landingDamage);
  }
  // A while after the last hurt, health comes back.
  p.sinceHurt += dt;
  if (p.sinceHurt > HEALTH.regenDelay) p.health = Math.min(HEALTH.max, p.health + HEALTH.regen * dt);
}

/** Take `amount` of health off a player (it can't go below 0; the server knocks them out at 0). */
export function hurt(p: PlayerState, amount: number): void {
  p.health = Math.max(0, p.health - amount);
  p.sinceHurt = 0;
}

/**
 * Vines (see VINES): G near one (not aboard a ship) grabs it (see movePlayer);
 * on it, you go hand over hand along it whichever way you push, in zero-g as
 * on a rock, with no fuel. G again (`letGo`) lets go; so does climbing off
 * either end (onto a lobe of its rock), or the vine being
 * cut. True if the step was spent on a vine.
 */
function climbVine(p: PlayerState, input: PlayerInput, letGo: boolean, center: Vec2, dt: number, sim: Sim): boolean {
  const cut = sim.vinesCut ?? new Set<string>();
  if (!p.vine) return false;
  const vine = sim.world.vines?.find((v) => v.id === p.vine);
  const curve = vine && !cut.has(vine.id) ? vineCurve(sim.world, vine) : null;
  if (!curve) {
    p.vine = null;
    p.vineT = undefined;
    return false;
  }
  const U = upVector(p.up);
  const tan = vineTangent(curve, p.vineT ?? 0);
  const along = clamp(vDot(vAdd(vScale(rightVector(p.up), input.moveX), vScale(U, input.moveY)), tan), -1, 1);
  const t = (p.vineT ?? 0) + (along * VINES.climbSpeed * dt) / Math.max(1, vineLength(curve));
  p.velocity = vScale(tan, along * VINES.climbSpeed);
  p.grounded = false;
  p.launched = false;
  if (letGo || t <= 0.02 || t >= 0.98) {
    // Off you come: onto the lobe at either end.
    p.vine = null;
    p.vineT = undefined;
    return true;
  }
  p.vineT = t;
  // Head turned the way the nearest rock pulls, if any.
  const field = gravityFieldAt(sim, center);
  if (field) p.up = wrapAngle(p.up + clamp(wrapAngle(upOnAsteroid(field, center, p.up) - p.up), -CHARACTER.turnRate * dt, CHARACTER.turnRate * dt));
  p.position = vSub(vinePoint(curve, t), vScale(upVector(p.up), CHARACTER.height / 2));
  return true;
}

/** Moving, climbing, swimming, flying: everything stepPlayer does but health. */
export function movePlayer(p: PlayerState, input: PlayerInput, dt: number, sim: Sim): void {
  p.lastInputSeq = input.seq;
  p.aim = { x: input.aimX, y: input.aimY };
  p.using = input.fire && !p.piloting && p.gunning === null;
  p.holding = input.hold ?? null;
  // Hooked on your grapple off the ground, up reels the line in (see GRAPPLE) instead of jumping, climbing or flying, until
  // you let go of it: reeled right in and the line off, it doesn't then kick you away.
  const reel = input.moveY > 0 && (!!p.reelHeld || (!!p.grapple && !p.frame && p.holding === "grapple" && !p.grounded));
  p.reelHeld = reel;
  const moveY = reel ? 0 : input.moveY;
  const jumpPressed = moveY > 0 && !p.jumpHeld;
  p.jumpHeld = input.moveY > 0;
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  if (p.frame && !ship) p.frame = null;
  // Up held off the ground, wearing a steampack, fires it: a tap still just jumps, and a second tap in the air still launches.
  // Not aboard, where up kicks you off the walls, nor while coasting from a launch.
  const jet = wearsSteampack(p.gear) && !ship && moveY > 0 && !p.grounded && !p.launched && !jumpPressed;

  if (p.piloting) {
    if (!ship || !helmUnder(p, ship) || input.interact) {
      p.piloting = false;
      if (ship?.state.pilotId === p.id) {
        ship.state.pilotId = null;
        ship.control = undefined;
      }
      if (input.interact) return;
    } else {
      ship.control = { x: input.moveX, y: input.moveY, turn: input.turn };
      p.velocity = v2();
      return;
    }
  }

  // Manning a turret: the mouse swings the gun; right click climbs back out.
  if (p.gunning !== null) {
    const host = turretHostOf(p, sim);
    const turret = host && pieceAnchor(host.grid, p.gunning) === p.gunning ? turretOf(host, p.gunning) : undefined;
    if (!host || !turret || input.interact) {
      if (turret?.gunner === p.id) turret.gunner = null;
      // A seat sunk into a ship's roof: back down under it, into the ship, not left standing in the roof to float out the top.
      if (ship && turret && input.interact) {
        const below = v2(p.position.x, Math.floor(p.position.y) - CHARACTER.height);
        if (!boxHitsShipTiles(characterBox(below, 0), ship.grid)) p.position = below;
      }
      p.gunning = null;
      p.gunningAsteroid = undefined;
      if (input.interact) return;
    } else {
      turret.gunner = p.id;
      turret.aim = turretAimAt(host, p.gunning, p.aim);
      p.velocity = v2();
      return;
    }
  }

  if (input.interact && ship && helmUnder(p, ship) && (ship.state.pilotId === null || ship.state.pilotId === p.id)) {
    p.piloting = true;
    ship.state.pilotId = p.id;
    p.velocity = v2();
    // Upright at the wheel, however you floated up to it.
    p.up = 0;
    return;
  }

  // Right click with your head (or middle) in a turret's seat (on your ship, or on an asteroid) climbs in, if nobody else is in it.
  const seat = input.interact ? seatHere(p, sim) : null;
  if (seat) {
    const turret = turretOf(seat.host, seat.anchor);
    if (!turret.gunner || turret.gunner === p.id) {
      turret.gunner = p.id;
      p.gunning = seat.anchor;
      const { grid } = seat.host;
      // Feet at the bottom middle of the seat, the turret turned as its grid turns there (see turnAt).
      const ax = seat.anchor % grid.w;
      const ay = Math.floor(seat.anchor / grid.w);
      const q = turnAt(grid, ax, ay);
      const inSeat = pieceToGrid(ax, ay, q, pieceDef(T.IRONCLAD_TURRET).w / 2, 0);
      if (ship) {
        p.position = inSeat;
        p.up = 0;
      }
      else {
        // On an asteroid, sit upright in it: the way the turret is turned.
        p.gunningAsteroid = hostId(seat.host);
        p.position = hostToWorld(seat.host, inSeat);
        p.up = wrapAngle((q * Math.PI) / 2);
      }
      p.velocity = v2();
      return;
    }
  }

  const C = CHARACTER;
  let pos = p.position;
  const center = vAdd(pos, vScale(upVector(p.up), C.height / 2));
  // Among the stream's rocks or a ring's the air goes with them (see flowAt): what follows steers and slows you against it, not still space.
  // Not aboard (the ship's your frame), nor riding a rock (it carries you itself, see carryRiders).
  const flow = ship || p.rode ? v2() : flowAt(sim.world, center);
  let vel = vSub(p.velocity, flow);

  // Vines (see VINES) take hold of you as you move: push any way with one in reach and you're on it. Not while
  // the steampack's burning (that lets go), nor for a moment after letting go with G. On a rock that pulls, only off the ground
  // or pushing up or down, so walking under one doesn't catch you. G grabs or lets go on purpose.
  const hookPressed = input.action === "hook";
  const pull = ship ? undefined : gravityFieldAt(sim, center);
  p.vineWait = Math.max(0, (p.vineWait ?? 0) - dt);
  const reaching = (input.moveX !== 0 || moveY !== 0) && !jet && !p.vineWait && (!pull || !p.grounded || moveY !== 0);
  const letGoVine = hookPressed && !!p.vine;
  if (!p.vine && !ship && (hookPressed || reaching)) {
    const near = vineNear(sim.world, sim.vinesCut ?? new Set<string>(), center, VINES.grabReach);
    if (near) {
      p.vine = near.vine.id;
      p.vineT = near.t;
      dropGrapple(p);
    }
  }
  // Let go with G: float free a moment before a vine takes hold again.
  if (letGoVine) p.vineWait = VINES.regrab;

  // Right click with the grapple in hand throws a line toward the mouse (or lets go of the one out).
  if (ship || p.holding !== "grapple") dropGrapple(p);
  else if (input.action === "grapple") {
    if (p.grappleOut != null) dropGrapple(p);
    else if (p.aim.x !== center.x || p.aim.y !== center.y) {
      p.grappleOut = 0;
      p.grappleDir = Math.atan2(p.aim.y - center.y, p.aim.x - center.x);
    }
  }
  // Flying out: it hooks the first rock or ship it reaches, or comes back empty at the end of its range.
  if (!p.grapple && p.grappleOut != null) {
    const out = Math.min(GRAPPLE.range, p.grappleOut + GRAPPLE.throwSpeed * dt);
    const dir = v2(Math.cos(p.grappleDir ?? 0), Math.sin(p.grappleDir ?? 0));
    const hit = grappleHit(sim, center, vAdd(center, dir), out);
    if (hit) {
      p.grapple = hit.at;
      p.grappleShip = hit.ship ?? null;
      p.grappleRock = hit.rock ?? null;
      p.grappleOut = vDist(center, grapplePoint(p, sim) ?? center);
    } else p.grappleOut = out < GRAPPLE.range ? out : null;
  }
  const hook = grapplePoint(p, sim);
  // The ship it was hooked into is gone.
  if (p.grapple && !hook) dropGrapple(p);

  if (!ship && climbVine(p, input, letGoVine, center, dt, sim)) return;

  let ladder: Vec2 | null = null;
  if (ship) ladder = ladderIn(ship.grid, 0, 0, center.x, center.y);
  else {
    for (const a of sim.asteroids) {
      ladder = ladderIn(a.grid, a.def.ox, a.def.oy, center.x, center.y);
      if (ladder) break;
    }
  }

  // Which way is up. Aboard a ship it's the ship's floor (up = 0 in its frame).
  // On a rock that pulls it's the grid direction nearest to out from the
  // centre (see upOnAsteroid), turned to over a few steps, so you can walk
  // all the way round through its four valleys. In open space it stays
  // however you were turned, and Q/E (input.turn, + counter-clockwise) spin you.
  const field = pull;
  if (ship) {
    // Aboard there's no gravity: on a ladder or holding onto the ship (see GRIP) you turn back upright with it; floating free, Q/E spin you.
    if (ladder || (!input.turn && holdsAt(pos, p.up, ship, sim).length)) p.up = wrapAngle(p.up + clamp(wrapAngle(-p.up), -C.turnRate * dt, C.turnRate * dt));
    else if (input.turn) p.up = wrapAngle(p.up + clamp(input.turn, -1, 1) * C.spinRate * dt);
  } else if (ladder) p.up = quarterTurn(p.up); // ladders are square to the grid
  else if (field && !p.launched) {
    const turn = wrapAngle(upOnAsteroid(field, center, p.up) - p.up);
    p.up = wrapAngle(p.up + clamp(turn, -C.turnRate * dt, C.turnRate * dt));
  } else if (!field && input.turn) {
    p.up = wrapAngle(p.up + clamp(input.turn, -1, 1) * C.spinRate * dt);
  }
  const R = rightVector(p.up);
  const U = upVector(p.up);
  p.jetting = false;
  /** Input pushes along the character's own right and up. */
  const push = vAdd(vScale(R, input.moveX), vScale(U, moveY));
  const pushing = vLen(push) > 0;
  const up = p.up;
  const blocked = ship
    ? (q: Vec2) => boxHitsShipTiles(characterBox(q, up), ship.grid)
    : (q: Vec2) => blockedForCharacter(q, up, sim);
  /** A steampack on their back (see STEAMPACK): without one there's no flying, only walking, jumping, climbing and the grapple. */
  const pack = wearsSteampack(p.gear);
  /** High over a rock (nothing below within reach): the steampack burns no steam there. */
  const high = pack && !!field && !p.grounded && !ladder && !grappleHit(sim, center, vSub(center, U), STEAMPACK.groundReach, false);
  /** The sides of you holding onto the ship you're aboard, or rock in zero-g (see GRIP). */
  let holds: Vec2[] = [];

  if (ladder) {
    // Climbing straight up or down centres you on the ladder so you fit through the hole in the floor.
    const vt =
      moveY !== 0 && input.moveX === 0
        ? vDot(vSub(ladder, center), R) * 12
        : approach(vDot(vel, R), input.moveX * C.walkSpeed * 0.6, C.groundAccel * dt);
    vel = vAdd(vScale(R, vt), vScale(U, moveY * C.climbSpeed));
    p.launched = false;
  } else if (!ship && waterAt(sim, center)) {
    // Swimming: move any way you like, but water holds you back.
    vel = vAdd(vel, vScale(push, C.swimAccel * dt));
    vel = vScale(vel, Math.max(0, 1 - C.swimDrag * dt));
    const speed = vLen(vel);
    if (speed > C.swimMaxSpeed) vel = vScale(vel, C.swimMaxSpeed / speed);
    p.launched = false;
  } else if (pack && ((!ship && !field && (jet || pushing)) || (jet && ((p.steam ?? 1) > 0 || high)))) {
    // The steampack: thrust the way you're pushing (or straight up), against
    // gravity if there is any. In open space it's free: any push fires it,
    // any way you like, and it burns no steam (only gravity costs you). It
    // never slows you by itself: the speed cap only bites on the way up to it.
    const dir = vLen(push) > 0 ? vNorm(push) : U;
    const cap = Math.max(STEAMPACK.maxSpeed, vLen(vel));
    vel = vAdd(vel, vScale(dir, STEAMPACK.thrust * dt));
    if (ship || field) {
      if (field) vel = vSub(vel, vScale(U, C.gravity * dt));
      if (!high) p.steam = Math.max(0, (p.steam ?? 1) - dt / STEAMPACK.burnTime);
    }
    const speed = vLen(vel);
    if (speed > cap) vel = vScale(vel, cap / speed);
    p.jetting = true;
    p.launched = false;
  } else if (p.launched) {
    // Coasting after a launch: nothing slows you, and nothing steers you.
    const speed = vLen(vel);
    if (speed > C.launchSpeed) vel = vScale(vel, C.launchSpeed / speed);
  } else if (!field && (holds = holdsAt(pos, up, ship, sim)).length) {
    // Holding onto the ship's floor, walls or ceiling, or rock in zero-g (see GRIP). Pushing away from every side you touch kicks you off (once a push; held, you
    // just carry on). Pushing along it crawls you that way, kept onto the sides you crawl along. Pushing nothing, you hold still.
    const dir = vNorm(push);
    if (pushing && holds.every((d) => vDot(dir, d) < -0.5)) {
      if (!p.pushHeld) vel = vScale(dir, GRIP.kick);
    } else {
      const along = holds.reduce((a, d) => (vDot(a, d) > 0 ? vSub(a, vScale(d, vDot(a, d))) : a), push);
      if (vLen(along) > 1e-6) {
        const crawl = vNorm(along);
        const stick = holds.filter((d) => Math.abs(vDot(crawl, d)) < 0.5).reduce((a, d) => vAdd(a, vScale(d, GRIP.stick)), v2());
        vel = vAdd(vScale(crawl, GRIP.crawlSpeed), stick);
      } else vel = vScale(vel, Math.max(0, 1 - GRIP.hold * dt));
    }
  } else if (ship) {
    // Floating free aboard, out of reach of anything to hold (see GRIP): the air slowly stops you, and pushing paddles you slowly through it.
    vel = vScale(vel, Math.max(0, 1 - GRIP.drag * dt));
    if (pushing) {
      const dir = vNorm(push);
      vel = vAdd(vel, vScale(dir, clamp(GRIP.paddleSpeed - vDot(vel, dir), 0, GRIP.paddle * dt)));
    }
  } else if (field) {
    const accel = p.grounded ? C.groundAccel : C.airAccel;
    const vt = approach(vDot(vel, R), input.moveX * C.walkSpeed, accel * dt);
    let vn = Math.max(-C.maxFall, vDot(vel, U) - C.gravity * dt);
    if (moveY > 0 && p.grounded) vn = C.jumpSpeed;
    vel = vAdd(vScale(R, vt), vScale(U, vn));
    if (jumpPressed && !p.grounded && !ship) {
      // Double jump on an asteroid: launch off it in the direction you're pushing.
      vel = vScale(vNorm(push), C.launchSpeed);
      p.launched = true;
    }
  } else {
    // Drifting (with nothing pressed, or no steampack to push with): the air slows you.
    vel = vScale(vel, Math.max(0, 1 - C.airDrag * dt));
    // Drifting in the stream or a ring: brought round to go with it, quicker than the air alone would.
    if (flow.x || flow.y) vel = vScale(vel, Math.max(0, 1 - STREAM.grip * dt));
    // Drifting in a current: it carries you along with it. (Not riding a rock: the rock carries you, see carryRiders.)
    const current = p.rode ? v2() : currentAt(sim.world, center);
    if (current.x || current.y) vel = vAdd(vel, vScale(vSub(current, vel), CURRENTS.grip * 2 * dt));
  }
  // Hooked: the line holds you at its length, and W reels it in. Jumping lets go, and so does being reeled right in onto something you stand on or hold.
  if (p.grapple && hook) {
    const to = vSub(hook, center);
    const dist = vLen(to);
    const d = vNorm(to);
    if ((jumpPressed && (p.grounded || !!field)) || (dist < GRAPPLE.letGo && (p.grounded || holds.length > 0))) dropGrapple(p);
    else {
      let len = p.grappleOut ?? dist;
      if (reel) {
        if (dist > GRAPPLE.letGo) {
          vel = vAdd(vel, vScale(d, GRAPPLE.pull * dt));
          const speed = vLen(vel);
          if (speed > GRAPPLE.maxSpeed) vel = vScale(vel, GRAPPLE.maxSpeed / speed);
        }
        len = Math.max(GRAPPLE.letGo, Math.min(len, dist));
        p.launched = false;
      }
      if (dist >= len) {
        // Taut: nothing carries you further out, and past its length it pulls you back.
        const inward = vDot(vel, d);
        const back = (dist - len) * GRAPPLE.stiffness;
        if (inward < back) vel = vAdd(vel, vScale(d, back - inward));
        p.launched = false;
      }
      p.grappleOut = len;
    }
  }
  if (input.moveX !== 0) p.facing = Math.sign(input.moveX);
  // The steampack refills on anything solid underfoot (or a ladder), and where it costs nothing (open space, high over a rock); not while it's burning.
  p.pushHeld = pushing;
  const burning = p.jetting && !!(ship || field) && !high;
  if (!burning && (p.grounded || ladder || holds.length > 0 || high || (!ship && !field)) && (p.steam ?? 1) < 1) p.steam = Math.min(1, (p.steam ?? 1) + dt / STEAMPACK.refillTime);
  // Back from the flow's frame to the world's, to move by.
  vel = vAdd(vel, flow);

  let vt = vDot(vel, R);
  let vn = vDot(vel, U);

  // Turning can tip the body into the ground or swing the head into a wall:
  // nudge it out to the nearest free spot (up first, then sideways).
  if (blocked(pos)) {
    const tries = [U, R, vScale(R, -1), vAdd(U, R), vSub(U, R)];
    let freed = false;
    for (let k = 1; k <= 24 && !freed; k++) {
      for (const d of tries) {
        const q = vAdd(pos, vScale(d, k * 0.1));
        if (!blocked(q)) {
          pos = q;
          freed = true;
          break;
        }
      }
    }
    // Still stuck (a tile was built on us): movement is unchecked so we can climb out, but never sink deeper.
    if (!freed) vn = Math.max(0, vn);
  }

  let rt = sweep(pos, R, vt * dt, blocked);
  if (rt.hit && p.grounded && vt !== 0) {
    // Walk up steps of up to two blocks (to your knees), so the lumpy, curved ground doesn't stop you, and ramps (gentle slopes, see shapes.ts) and deck edges aboard a ship are walked up.
    const moved = vDist(rt.pos, pos);
    for (const lift of [0.7, 1.4, 2.1]) {
      const lifted = vAdd(pos, vScale(U, lift));
      if (blocked(lifted)) break;
      const r2 = sweep(lifted, R, vt * dt, blocked);
      if (!r2.hit || vDist(r2.pos, lifted) > moved + 1e-3) {
        rt = { pos: sweep(r2.pos, U, -(lift + 0.1), blocked).pos, hit: r2.hit };
        break;
      }
    }
  }
  pos = rt.pos;
  if (rt.hit) vt = 0;
  const rn = sweep(pos, U, vn * dt, blocked);
  pos = rn.pos;
  const wasGrounded = p.grounded;
  p.grounded = rn.hit && vn < 0;
  if (rn.hit) vn = 0;
  if (!ship && field && wasGrounded && !p.grounded && vn <= 0 && !p.launched) {
    // Stick to the ground when walking over a crest instead of skipping off it.
    const snap = sweep(pos, U, -0.6, blocked);
    if (snap.hit) {
      pos = snap.pos;
      p.grounded = true;
      vn = 0;
    }
  }
  vel = vAdd(vScale(R, vt), vScale(U, vn));
  // A launch ends when you hit something, or once you're out of the rock's gravity (then you're just drifting).
  if (p.launched && (rt.hit || rn.hit || !field)) {
    p.launched = false;
  }

  p.position = pos;
  p.velocity = vel;
  updateFrame(p, sim);
}

/** Their grapple line, hooked or still flying out, is gone. */
export function dropGrapple(p: PlayerState): void {
  p.grapple = null;
  p.grappleShip = null;
  p.grappleRock = null;
  p.grappleOut = null;
}

/** Whether a player is holding onto the ship they're aboard, or onto rock out in zero-g (see GRIP). */
export function holdingOn(p: PlayerState, sim: Sim): boolean {
  const ship = p.frame ? sim.ships.get(p.frame) : undefined;
  if (!ship && inGravityField(sim, playerWorldCenter(p, sim))) return false;
  return holdsAt(p.position, p.up, ship, sim).length > 0;
}

/**
 * Which of a body's four sides (its right, up and their backs) touch something to hold within GRIP.reach: the
 * ship's own tiles aboard `ship`, else asteroid rock.
 */
function holdsAt(feet: Vec2, up: number, ship: ShipBody | undefined, sim: Sim): Vec2[] {
  const R = rightVector(up);
  const U = upVector(up);
  const touches = ship ? (q: Vec2) => boxHitsShipTiles(characterBox(q, up), ship.grid) : (q: Vec2) => rockHitsCharacter(q, up, sim);
  return [R, U, vScale(R, -1), vScale(U, -1)].filter((d) => touches(vAdd(feet, vScale(d, GRIP.reach))));
}

/**
 * Hop between world space and a ship's frame. A character is aboard while
 * their body centre is over any of the ship's tiles (back walls included), so
 * walking out through a hatch drops them into world space with the ship's
 * velocity, and walking in picks them up.
 */
export function updateFrame(p: PlayerState, sim: Sim): void {
  if (p.piloting || p.gunning !== null) return;
  const hc = CHARACTER.height / 2;
  if (p.frame) {
    const ship = sim.ships.get(p.frame);
    if (ship && tileAt(ship.grid, Math.floor(p.position.x), Math.floor(p.position.y + hc)) !== T.EMPTY) return;
    if (ship) leaveShip(p, ship);
    p.frame = null;
    return;
  }
  const center = vAdd(p.position, vScale(upVector(p.up), hc));
  for (const ship of sim.ships.values()) {
    if (vDist(center, shipCenter(ship)) > ship.stats.radius) continue;
    const local = worldToShip(ship, center);
    if (tileAt(ship.grid, Math.floor(local.x), Math.floor(local.y)) === T.EMPTY) continue;
    enterShip(p, ship, local);
    return;
  }
}
